import React, { useEffect, useState } from 'react'
import AddressSummaryCard from 'modules/dashboards/actionables_v2/AddressSummaryCard';
import _ from 'lodash';
import { useAddressStore } from 'modules/dashboards/actionables_v2/store';
import AddressSelectionModal from 'modules/dashboards/actionables_v2/AddressSelectionModal';

// address2 is required here even though /product-change treats it as optional: the place-order and
// prepaid-link flows both refuse to submit without it, because logistics needs the landmark to
// deliver. The tickets are already raised by the time the order is placed, so an incomplete saved
// address is called out on the card instead of failing later.
const REQUIRED_FIELDS = ['address1', 'address2', 'city', 'province', 'zip'];

// The saved-address objects are already in the shape /product-change validates, so this is a pick
// rather than a translation.
const toShopifyAddress = (address) => {
    if (!address) return null
    if (REQUIRED_FIELDS.some(field => _.isEmpty(_.trim(_.get(address, field, ''))))) return null

    const country = _.trim(_.get(address, 'country', ''))
    const countryCode = _.trim(_.get(address, 'country_code', ''))

    return {
        address1: _.trim(_.get(address, 'address1', '')),
        address2: _.trim(_.get(address, 'address2', '')),
        city: _.trim(_.get(address, 'city', '')),
        province: _.trim(_.get(address, 'province', '')),
        zip: _.trim(_.get(address, 'zip', '')),
        // Joi treats these as optional strings, so blanks are omitted rather than sent empty.
        ...(country ? { country } : {}),
        ...(countryCode ? { country_code: countryCode } : {}),
    }
}

const ReplacementAddress = ({ customerData, setTicketPayload }) => {
    // Read straight from the shared address store, exactly as the place-order form does — the
    // selection lives there and AddressSelectionModal writes it, so nothing here is re-implemented.
    const { fetchAddresses, isLoading, selectedAddress } = useAddressStore();
    const [showAddressModal, setShowAddressModal] = useState(false);

    const userId = _.get(customerData, ['user', 'id'], null);

    // The modal builds a new address's name/phone from these, the same fields the place-order flow feeds it.
    const customerDetails = {
        first_name: _.get(customerData, ['user', 'first_name'], ''),
        last_name: _.get(customerData, ['user', 'last_name'], ''),
        email: _.get(customerData, ['user', 'email'], ''),
        phone: _.get(customerData, ['user', 'phone_number'], ''),
    };

    // Loads the customer's addresses and settles on their default, the same call the place-order panel makes.
    useEffect(() => {
        if (userId) fetchAddresses(userId)
    }, [userId, fetchAddresses])

    useEffect(() => {
        setTicketPayload(prevPayload => ({
            ...prevPayload,
            replacement_address: toShopifyAddress(selectedAddress),
        }))
    }, [selectedAddress])

    // Teardown is its own mount-scoped effect: folding it into the sync effect above stripped and
    // re-added the key on every change, leaving correctness resting on update batching.
    useEffect(() => () => setTicketPayload(({ replacement_address, ...restOfPayload }) => restOfPayload), [])

    const isMalformed = Boolean(selectedAddress) && toShopifyAddress(selectedAddress) === null

    return (
        <>
            <AddressSummaryCard
                label='Replacement Delivery Address'
                address={selectedAddress}
                isLoading={isLoading}
                onChangeClick={() => setShowAddressModal(true)}
                changeDisabled={!userId}
                error={!userId ? 'Could not identify the customer for this order, so saved addresses cannot be loaded.' : null}
            >
                {isMalformed && (
                    <p className='text-danger mb-0 mt-1' style={{ fontSize: 12 }}>
                        This address is missing one of Address Line 1, Address Line 2, City, State or Pincode. Pick another one or edit it, otherwise the replacement will ship to the customer's default address.
                    </p>
                )}
            </AddressSummaryCard>

            <AddressSelectionModal
                show={showAddressModal}
                onHide={() => setShowAddressModal(false)}
                userId={userId}
                customerDetails={customerDetails}
            />
        </>
    );
};

export default ReplacementAddress;
